import {
  ErrorComponent,
  ErrorComponentProps,
  Outlet,
  RouterProvider,
  createRootRoute,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { Button, Collapse, NonIdealState } from "@blueprintjs/core";
import { App } from "src/app/App";
import { JM_ENVIRONMENT } from "src/app/Firebase";
import { RoutedComponentStackProvider } from "./currentRoutedComponent";
import { getRouterFallbackHref } from "./routerFallbackHref";
import { captureAppError } from "lib/sentry/errorReporting";
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { useEffect, useState } from "react";
import { DevEnvironmentFavicon } from "../DevEnvironmentIndicator";

export function RouterErrorFallback(props: ErrorComponentProps) {
  useEffect(() => {
    captureAppError({
      error: props.error,
      componentName: "RouterErrorFallback",
      consoleMessage: "Router error",
      fallbackMessage: "An error occurred when routing to this screen.",
    });
  }, [props.error]);

  return <RouterErrorComponent {...props} />;
}

const RouterErrorComponent = ({ error, reset }: ErrorComponentProps) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <NonIdealState
      title="An unexpected error occurred"
      icon="error"
      description={
        <div className="flex flex-col gap-2 mx-2">
          <p>There was an unexpected error when routing to this screen.</p>
          <button
            type="button"
            className="underline"
            onClick={() => setIsOpen(!isOpen)}
          >
            {isOpen ? "Hide Details" : "Show Details"}
          </button>
          <Collapse
            isOpen={isOpen}
            className={`text-left p-1 rounded-lg max-h-48 overflow-y-scrollbar ${isOpen ? "bg-light-gray-5 border" : ""}`}
          >
            Name: {error.name}
            <br />
            Message: {error.message}
            <br />
            Stack: {error.stack}
          </Collapse>
        </div>
      }
      action={
        <div className="flex gap-3">
          <Button icon="refresh" text="Retry" onClick={reset} />
          <Button
            text="Go Home"
            icon="home"
            intent="primary"
            onClick={() => {
              window.location.assign(getRouterFallbackHref(JM_ENVIRONMENT));
            }}
          />
        </div>
      }
    />
  );
};

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  errorComponent: RouterErrorFallback,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: App,
});

const componentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  component: App,
});

const devToolsCrashRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "__devtools/crash",
  component: () => {
    throw new Error("Test Router crash");
  },
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  devToolsCrashRoute,
  componentRoute,
]);

export const appRouter = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof appRouter;
  }
}

export function AppRouterProvider() {
  return (
    <RoutedComponentStackProvider>
      <RouterProvider router={appRouter} />
      <DevEnvironmentFavicon />
      {/* <TanStackRouterDevtools router={appRouter} /> */}
    </RoutedComponentStackProvider>
  );
}
